/**
 * NativeLockScreenRefill
 *
 * Native-only. Pushes the "what to play next while the screen is locked"
 * context into the native plugin (AVPlayer / ExoPlayer), so the queue can
 * extend itself without going through suspended JS.
 *
 * Strict rules this file follows:
 *   - Does NOT mutate Redux (read-only consumer).
 *   - Does NOT touch MusicPlayer.js / musicPlayerSlice.js.
 *   - Renders nothing — pure side-effect component.
 *   - No-op on web/Electron.
 *
 * What it sends to native:
 *   - listContextItems: the surface's track list mapped to {title, artist,
 *     sampleUrl, artworkUrl}. Native pops from here first.
 *   - listContextStartIdx: the next unconsumed index, counted in the SAME
 *     filtered array we send (not listContext.currentIndex + 1 — see below).
 *   - seedTitle / seedArtist: the current track, used to seed the recs
 *     endpoint when listContext exhausts.
 *   - playedKeys: same dedup keys autoRadio uses on the JS side
 *     ("Artist|||Title"), so native skips already-heard recs.
 *   - apiHost + recsPath + refreshPath: the recs endpoint and the OAuth
 *     refresh endpoint, used by native HTTP refill + 401-recovery.
 *   - accessToken + refreshToken: short-lived JWT + long-lived refresh
 *     token. Native swaps the access token via the refresh endpoint when
 *     recs return 401 and retries.
 *
 * Mix surfaces (`listContext.type === 'mixes'`) are intentionally skipped
 * — mix URLs need yt-dlp extraction, which the native plugin doesn't do.
 * Track surfaces (For You, Mixpedia track results, Crate Digger, Liked
 * Tracks' track portion) all use sample URLs that play natively as-is.
 */

import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getNativePlaybackEngine } from '../playback/NativePlaybackEngine';
import { isNative } from '../utils/webOnly';

// API constants. Matches what capacitor-shim.js uses for the same calls
// on web (when we go through JS instead).
const API_HOST = 'https://app.mixgoat.com';
const RECS_PATH = '/api/djpal/tracks/recommendations/search';
const REFRESH_PATH = '/api/auth/refresh';
const DJPAL_TOKEN_PATH = '/api/auth/djpal-token';
const ALGORITHM = 'NODE2VEC';
const VARIANT = 'A';

function NativeLockScreenRefill() {
    const listContextItems = useSelector((s) => s.listContext.items);
    const listContextIdx = useSelector((s) => s.listContext.currentIndex);
    const listContextType = useSelector((s) => s.listContext.type);
    const accessToken = useSelector((s) => s.auth.accessToken);
    const refreshToken = useSelector((s) => s.auth.refreshToken);
    // The recs endpoint authenticates with the DJPAL token (30-day), NOT the
    // MixGoat access token — posting the access token is a guaranteed 401
    // (verified live 2026-08-14), which left the native refill permanently
    // empty and the player parked at end-of-queue on every playlist. The
    // slice hydrates from localStorage 'djpal:token' and the renewal loop
    // keeps it fresh, so this selector is a live dependency.
    const djpalToken = useSelector((s) => s.djpal?.token || '');
    const playedTrackKeys = useSelector((s) => s.musicPlayer.playedTrackKeys);
    const currentTrackName = useSelector((s) => s.musicPlayer.currentTrack?.name || '');
    const currentTrackSinger = useSelector((s) => s.musicPlayer.currentTrack?.singer || '');
    const currentTrackCover = useSelector((s) => s.musicPlayer.currentTrack?.cover || '');

    // Mini-player artwork + title + artist sync. Jinke renders these
    // from its audioList prop, which on native only ever has the
    // originally-tapped track. When the native plugin auto-advances,
    // the mini-player keeps showing the stale info. Patch the visible
    // DOM directly from Redux currentTrack whenever it changes.
    useEffect(() => {
        if (!isNative) return undefined;
        const assertDom = () => {
            try {
                if (currentTrackCover) {
                    const img = document.querySelector('.react-jinke-music-player-main .img-content');
                    const want = `url("${currentTrackCover}")`;
                    if (img && img.style.backgroundImage !== want) img.style.backgroundImage = want;
                }
                if (currentTrackName) {
                    const titleEl = document.querySelector('.react-jinke-music-player-main .audio-title .mixgoat-mini-title-line');
                    if (titleEl && titleEl.textContent !== currentTrackName) titleEl.textContent = currentTrackName;
                }
                if (currentTrackSinger || currentTrackName) {
                    const artistEl = document.querySelector('.react-jinke-music-player-main .audio-title .mixgoat-mini-artist-line');
                    const wantArtist = currentTrackSinger || '';
                    if (artistEl && artistEl.textContent !== wantArtist) artistEl.textContent = wantArtist;
                }
            } catch (_) { /* DOM may not be ready yet — the interval retries */ }
        };
        assertDom();
        // Instant revert on outside writes: jinke re-renders these nodes from
        // its OWN audioList — the moment Add-to-Next-Up appends an entry it
        // repainted the ADDED track's cover, which showed for up to a second
        // until the next tick (user report 2026-08-21, iOS). A
        // MutationObserver runs at the microtask checkpoint — BEFORE paint —
        // so the compare-first re-assert lands with no visible flash. The
        // write inside assertDom re-fires the observer once; the compare then
        // matches and the chain stops.
        let mo = null;
        let observed = null;
        const hook = () => {
            const img = document.querySelector('.react-jinke-music-player-main .img-content');
            if (!img || img === observed) return;
            if (mo) mo.disconnect();
            observed = img;
            mo = new MutationObserver(assertDom);
            mo.observe(img, { attributes: true, attributeFilter: ['style'] });
        };
        hook();
        // Slow tick stays: it covers the title/artist nodes, the first mount
        // (node not in the DOM yet), and a jinke remount replacing the
        // observed node.
        const iv = setInterval(() => { assertDom(); hook(); }, 1000);
        return () => { clearInterval(iv); if (mo) mo.disconnect(); };
    }, [currentTrackCover, currentTrackName, currentTrackSinger]);

    // Dedup: native's playerState fires at 4Hz and brings the current
    // track's title/artist along, which Redux mirrors into currentTrack.
    // Each tick triggers this useEffect even when nothing relevant changed.
    // Compare a tiny signature of the last-pushed payload and skip
    // redundant IPC calls.
    const lastSigRef = useRef('');

    useEffect(() => {
        if (!isNative) return;
        const engine = getNativePlaybackEngine();
        if (!engine.isAvailable()) return;

        // Normalize to the shape native expects, dropping anything with no
        // playable sample — AND translate the start index into the filtered
        // array as we go.
        //
        // Those two things have to happen together. The start index used to be
        // computed as `listContext.currentIndex + 1`, which is a position in
        // the UNFILTERED list, then applied by the plugin to this filtered
        // one. Every preview-less row before the current track shifted the
        // pool forward by one, so the next track came from further down the
        // feed than the user's position — reported as "played the first track,
        // hit next, and it jumped to track 7".
        //
        // startIdx counts playable items at or before the current one, which
        // IS the index of the first unconsumed playable item. When nothing is
        // dropped it equals currentIndex + 1, exactly as before.
        const isTrackList = listContextType === 'tracks' && Array.isArray(listContextItems);
        const nativeItems = [];
        let startIdx = 0;
        if (isTrackList) {
            listContextItems.forEach((t, i) => {
                if (!t) return;
                const mapped = {
                    title: t.title || t.name || '',
                    artist: t.artist || t.djName || t.dj_name || '',
                    sampleUrl: t.previewUrl || t.sampleUrl || t.preview_url || t.preview || '',
                    // Thumbnail — the plugin upscales for its own lock-screen
                    // rendering. Sending the big url from here leaked it back
                    // through playerState into currentTrack.cover, which the
                    // now-playing hero renders.
                    artworkUrl: t.artwork || t.artworkUrl || t.image || t.cover || '',
                };
                if (!mapped.title || !mapped.sampleUrl) return;
                nativeItems.push(mapped);
                if (i <= (listContextIdx | 0)) startIdx = nativeItems.length;
            });
            // The pool starts EXHAUSTED. MusicPlayer now hands the plugin this
            // entire surface up front via setQueue, so serving it again here
            // would queue every track twice. The items are still sent because
            // the plugin uses their identity to tell a context refresh from a
            // genuinely new context (and only wipes its played-keys dedup on
            // the latter); what it does past the end of the queue is the recs
            // fallback, seeded from whatever is playing then.
            startIdx = nativeItems.length;
        }

        // Signature of the parts native cares about. itemsLen acts as a
        // proxy for the items array identity — listContextItems is the
        // Redux state reference and changes only on a fresh listContext.
        const sig = [
            nativeItems.length,
            startIdx,
            currentTrackName,
            currentTrackSinger,
            (playedTrackKeys || []).length,
            accessToken || '',
            refreshToken || '',
            djpalToken || '',
        ].join('|');
        if (sig === lastSigRef.current) return;
        lastSigRef.current = sig;

        engine.setRefillContext({
            listContextItems: nativeItems,
            listContextStartIdx: startIdx,
            seedTitle: currentTrackName,
            seedArtist: currentTrackSinger,
            playedKeys: Array.isArray(playedTrackKeys) ? playedTrackKeys : [],
            apiHost: API_HOST,
            recsPath: RECS_PATH,
            refreshPath: REFRESH_PATH,
            algorithm: ALGORITHM,
            variant: VARIANT,
            accessToken: accessToken || '',
            refreshToken: refreshToken || '',
            // Recs bearer. On 401 the plugin refreshes the ACCESS token and
            // re-exchanges it at djpalTokenPath for a fresh one of these.
            djpalToken: djpalToken || '',
            djpalTokenPath: DJPAL_TOKEN_PATH,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        listContextItems,
        listContextIdx,
        listContextType,
        accessToken,
        refreshToken,
        djpalToken,
        playedTrackKeys,
        currentTrackName,
        currentTrackSinger,
    ]);

    return null;
}

export default NativeLockScreenRefill;
